import { Badge, Button, Input, Select, Switch } from '@cloudflare/kumo';
import { useState, type ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, type ThemePreference } from '../contexts/ThemeContext';
import { useNotifications } from '../hooks/useNotifications';
import { LANGUAGES, TIMEZONES, usePreference } from '../lib/preferences';
import { AppTabs } from '../components/ui/AppTabs';
import { notificationLink } from '../lib/notificationLink';
import { ICONS, Glyph } from '../components/ui/icons';
import type { UseNotificationsReturn } from '../hooks/useNotifications';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { SkeletonText } from '../components/ui/Skeleton';

const THEMES: Array<{ value: ThemePreference; label: string }> = [
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Escuro' },
  { value: 'system', label: 'Sistema' },
];

/** Cartão de configuração: título à esquerda, controle à direita (como a página Profile da Cloudflare). */
function SettingCard({ title, badge, children }: { title: string; badge?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-kumo-line bg-kumo-base p-5">
      <div className="mb-3 flex items-center gap-2 text-base font-semibold text-kumo-default">{title}{badge}</div>
      {children}
    </section>
  );
}

function SettingRow({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex items-center justify-between gap-4 rounded-lg border border-kumo-line bg-kumo-base p-5">
      <div className="text-base font-semibold text-kumo-default">{title}</div>
      <div className="w-56 max-w-[55%]">{children}</div>
    </section>
  );
}

function SettingsTab() {
  const { user, setUser } = useAuth();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [nome, setNome] = useState(user?.nome ?? '');
  const [saving, setSaving] = useState(false);
  const saveName = async () => {
    setSaving(true);
    try {
      const r = await fetchApi('/api/account/profile', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nome }) });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(body.error || 'Não foi possível atualizar'); return; }
      if (user) setUser({ ...user, nome: body.user.nome });
      toast.success('Nome atualizado');
    } finally {
      setSaving(false);
    }
  };
  const { preference, setPreference } = useTheme();
  const [language, setLanguage] = usePreference('language', 'pt-BR');
  const [timezone, setTimezone] = usePreference('timezone', 'America/Sao_Paulo');
  const items = (list: Array<[string, string]>) => list.map(([value, label]) => ({ value, label }));

  return (
    <div className="mx-auto flex max-w-[800px] flex-col gap-4">
      <SettingCard title="Nome de exibição">
        <div className="flex gap-3">
          <Input aria-label="Nome de exibição" className="flex-1" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={100} />
          <Button variant="secondary" loading={saving} disabled={saving || nome.trim() === (user?.nome ?? '') || nome.trim().length < 2} onClick={() => void saveName()}>Atualizar</Button>
        </div>
      </SettingCard>
      <SettingCard title="E-mail" badge={<Badge variant="primary">Verificado</Badge>}>
        <Input aria-label="E-mail" value={user?.email ?? ''} readOnly />
      </SettingCard>
      <SettingRow title="Idioma">
        <Select aria-label="Idioma" value={language} items={items(LANGUAGES)} onValueChange={(v: string | null) => v && setLanguage(v)} />
      </SettingRow>
      <SettingRow title="Aparência">
        <Select aria-label="Aparência" value={preference} items={THEMES} onValueChange={(v: string | null) => v && setPreference(v as ThemePreference)} />
      </SettingRow>
      <SettingRow title="Fuso horário">
        <Select aria-label="Fuso horário" value={timezone} items={items(TIMEZONES)} onValueChange={(v: string | null) => v && setTimezone(v)} />
      </SettingRow>
    </div>
  );
}

const NOTIF_SECTIONS: Array<{ title: string; items: Array<{ id: string; title: string; text: string }> }> = [
  { title: 'Marketing', items: [
    { id: 'blog', title: 'Blog', text: 'Fique por dentro das novidades e tendências da plataforma.' },
    { id: 'educacao', title: 'Educação e recursos', text: 'Aproveite mais a sua assinatura com conteúdos e materiais de apoio.' },
    { id: 'eventos', title: 'Eventos', text: 'Receba avisos de eventos, treinamentos e webinars.' },
    { id: 'produto', title: 'Novidades do produto e promoções', text: 'Saiba de novos recursos e promoções assim que forem lançados.' },
  ] },
  { title: 'Organização', items: [
    { id: 'resumo', title: 'Resumo semanal', text: 'Um resumo de conversas, negócios e uso de IA da sua organização.' },
    { id: 'alertas', title: 'Alertas de agentes', text: 'Avise quando um agente de IA tiver muitos erros ou ficar lento.' },
  ] },
];

const PAGE_SIZE = 10;

function ago(d: string): string {
  const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `há ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `há ${h} h`;
  const days = Math.floor(h / 24);
  return days < 30 ? `há ${days} d` : new Date(d).toLocaleDateString('pt-BR');
}

function RecentNotifications({ n, onOpen }: { n: UseNotificationsReturn; onOpen: (path: string) => void }) {
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [page, setPage] = useState(1);
  const list = onlyUnread ? n.notifications.filter((x) => !x.read) : n.notifications;
  const pages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const rows = list.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);
  return (
    <div className="flex flex-col gap-4">
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold text-kumo-default">
          Recentes {n.unreadCount > 0 && <Badge variant="primary">{n.unreadCount} não lidas</Badge>}
        </h2>
        <div className="flex gap-2">
          <Button variant={onlyUnread ? 'primary' : 'secondary'} size="sm" onClick={() => { setOnlyUnread((v) => !v); setPage(1); }}>Não lidas</Button>
          <Button variant="secondary" size="sm" disabled={n.unreadCount === 0} onClick={() => void n.markAllAsRead()}>Marcar todas como lidas</Button>
        </div>
      </div>
      {n.loading ? (
        <SkeletonText lines={3} label="Carregando" />
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-kumo-line bg-kumo-base p-10 text-center">
          <Glyph icon={ICONS.Bell} size={28} className="text-kumo-subtle" />
          <div className="font-semibold text-kumo-default">{onlyUnread ? 'Nenhuma notificação não lida' : 'Nenhuma notificação'}</div>
          <p className="text-sm text-kumo-subtle">Alertas de conexão, campanhas, cota e agentes aparecem aqui.</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((x) => {
            const link = notificationLink(x.data, x.alertType);
            return (
              <li key={x.id} className={`flex items-start gap-3 rounded-lg border border-kumo-line p-4 ${x.read ? 'bg-kumo-base' : 'bg-kumo-tint'}`}>
                {!x.read && <span className="mt-2 size-2 shrink-0 rounded-full bg-kumo-brand" aria-label="Não lida" />}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold text-kumo-default">{x.title}</span>
                    <span className="text-xs text-kumo-subtle">{ago(x.createdAt)}</span>
                  </div>
                  <p className="mt-1 text-sm text-kumo-subtle">{x.message}</p>
                  <div className="mt-2 flex gap-2">
                    {link && <Button size="sm" variant="secondary" onClick={() => { if (!x.read) n.markAsRead(x.id); onOpen(link); }}>Abrir</Button>}
                    {!x.read && <Button size="sm" variant="ghost" onClick={() => n.markAsRead(x.id)}>Marcar como lida</Button>}
                    <Button size="sm" variant="ghost" onClick={() => void n.deleteNotification(x.id)}>Excluir</Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {list.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-kumo-subtle">
          <span>Mostrando {(cur - 1) * PAGE_SIZE + 1}–{Math.min(cur * PAGE_SIZE, list.length)} de {list.length}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={cur <= 1} onClick={() => setPage(cur - 1)}>Anterior</Button>
            <Button size="sm" variant="secondary" disabled={cur >= pages} onClick={() => setPage(cur + 1)}>Próxima</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationsTab() {
  const navigate = useNavigate();
  const n = useNotifications();
  const [prefs, setPrefs] = usePreference('notificacoes', '{}');
  let map: Record<string, boolean> = {};
  try { map = JSON.parse(prefs) as Record<string, boolean>; } catch { map = {}; }
  return (
    <div className="mx-auto flex max-w-[800px] flex-col gap-4">
      {NOTIF_SECTIONS.map((sec) => (
        <div key={sec.title} className="flex flex-col gap-4">
          <h2 className="mt-4 text-2xl font-semibold text-kumo-default">{sec.title}</h2>
          {sec.items.map((it) => (
            <section key={it.id} className="flex items-start justify-between gap-4 rounded-lg border border-kumo-line bg-kumo-base p-5">
              <div>
                <div className="text-base font-semibold text-kumo-default">{it.title}</div>
                <p className="mt-1 text-sm text-kumo-subtle">{it.text}</p>
              </div>
              <Switch aria-label={it.title} checked={!!map[it.id]} onCheckedChange={(v: boolean) => setPrefs(JSON.stringify({ ...map, [it.id]: v }))} />
            </section>
          ))}
        </div>
      ))}
      <RecentNotifications n={n} onOpen={(path) => navigate(path)} />
      <p className="text-xs text-kumo-subtle">As preferências ficam salvas neste navegador.</p>
    </div>
  );
}

/** Perfil do usuário: configurações pessoais e notificações. */
export function PerfilPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const aba = params.get('aba') || 'configuracoes';
  const since = user?.criadoEm ? new Date(user.criadoEm).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  return (
    <div className="pb-10">
      <div className="px-4 pb-4 pt-7 md:px-10">
        <h1 className="text-3xl font-semibold text-kumo-default">Perfil</h1>
        <p className="mt-1 text-kumo-subtle">{user?.email}{since ? ` · Membro desde ${since}` : ''}</p>
      </div>
      <div className="px-4 md:px-10">
        <AppTabs
          ariaLabel="Seções do perfil"
          activeTabId={aba}
          onChange={(e) => setParams(e.detail.activeTabId === 'configuracoes' ? {} : { aba: e.detail.activeTabId })}
          tabs={[
            { id: 'configuracoes', label: 'Configurações', content: <SettingsTab /> },
            { id: 'notificacoes', label: 'Notificações', content: <NotificationsTab /> },
          ]}
        />
      </div>
    </div>
  );
}

export default PerfilPage;
